import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  MODULE_CATALOG,
  type WriteOperationProductGroup,
  type WriteOperationSummary,
} from '@vce/shared';
import { PageHeader } from '@/app/layout';
import { useAuth } from '@/app/auth-context';
import { useTenantReady } from '@/app/tenant-context';
import { NoTenantState } from '@/components/no-tenant-state';
import { PaginationButtons } from '@/components/pagination-buttons';
import { DiffPanel } from '@/components/suggestion-diff';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  FIELD_LABEL,
  QueryState,
  Select,
  Spinner,
} from '@/components/ui/primitives';
import { api, ApiError } from '@/lib/api';
import { useRunsModuleCta } from '@/lib/module-cta';
import { formatDateTime } from '@/lib/utils';

const PAGE_SIZE = 50;

const STATUS_TONE: Record<string, 'neutral' | 'success' | 'critical' | 'medium' | 'brand'> = {
  pending: 'neutral',
  dry_run: 'brand',
  succeeded: 'success',
  failed: 'critical',
  aborted_conflict: 'medium',
  rolled_back: 'neutral',
};

const STATUS_LABEL: Record<string, string> = {
  pending: 'pendente',
  dry_run: 'simulação',
  succeeded: 'publicado',
  failed: 'falhou',
  aborted_conflict: 'abortado por conflito',
  rolled_back: 'revertido',
};

const PRODUCT_FIELD_KEYS: Record<string, string> = {
  name: 'Name',
  title: 'Title',
  description: 'Description',
  metaTagDescription: 'MetaTagDescription',
  keywords: 'KeyWords',
  skuName: 'Name',
};

const IMAGE_FIELD_KEYS: Record<string, string> = {
  imageAltText: 'Text',
  imageLabel: 'Label',
};

const PANEL_DESCRIPTION =
  'Cada publicação guarda o estado antes do envio à VTEX. O rollback restaura por operação, não o grupo inteiro.';

/**
 * Corpo de Publicações (write-operations).
 * Compartilhado por `/runs?tab=publications` (embedded) e `/write-operations` (standalone).
 * Uma linha-pai por produto; operações filhas expansíveis com rollback individual.
 */
export function WriteOperationsPanel({ embedded = false }: { embedded?: boolean }) {
  const { tenantId, isResolving } = useTenantReady();
  const { can } = useAuth();
  const runsCta = useRunsModuleCta();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('');
  const [expandedProduct, setExpandedProduct] = useState<number | null>(null);
  const [expandedOp, setExpandedOp] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ['write-operations', tenantId, status, page],
    queryFn: () =>
      api.writeOperations.list(tenantId as string, {
        status: status || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    enabled: tenantId !== null,
  });

  const rollback = useMutation({
    mutationFn: (id: string) => api.writeOperations.rollback(tenantId as string, id),
    onSuccess: () => {
      toast.success('Rollback enfileirado — o produto volta ao estado pré-publicação');
      void queryClient.invalidateQueries({ queryKey: ['write-operations', tenantId] });
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'Falha ao reverter'),
  });

  if (isResolving) {
    return <Spinner label="Carregando conta…" />;
  }

  if (!tenantId) {
    return <NoTenantState />;
  }

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const statusFilter = (
    <Select
      className="w-full sm:w-auto"
      value={status}
      onChange={(event) => {
        setStatus(event.target.value);
        setPage(1);
      }}
    >
      <option value="">Todos os status</option>
      {Object.entries(STATUS_LABEL).map(([value, label]) => (
        <option key={value} value={value}>
          {label}
        </option>
      ))}
    </Select>
  );

  return (
    <>
      {!embedded ? (
        <PageHeader
          title={MODULE_CATALOG.write_operations.label}
          description={PANEL_DESCRIPTION}
          actions={statusFilter}
        />
      ) : (
        <div className="flex w-full flex-wrap items-center gap-2 border-b border-[var(--color-hairline)] px-4 py-3 sm:px-6">
          <p className="mr-auto min-w-0 flex-1 text-sm text-[var(--color-muted)] sm:flex-none sm:basis-full lg:basis-auto">
            {PANEL_DESCRIPTION}
          </p>
          {statusFilter}
        </div>
      )}

      <div className="space-y-3 p-4 sm:p-6">
        <Card>
          <QueryState
            isLoading={query.isLoading}
            isError={query.isError}
            errorTitle="Não foi possível carregar o histórico"
            onRetry={() => void query.refetch()}
          >
            {items.length === 0 ? (
              <EmptyState
                title="Nenhuma publicação registrada"
                description={runsCta.description(
                  `Simule ou execute uma escrita em ${MODULE_CATALOG.runs.label}.`,
                )}
                action={runsCta.action()}
              />
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full text-sm">
                    <thead className="bg-[var(--color-surface-soft)] text-left text-xs text-[var(--color-muted)]">
                      <tr>
                        <th className="px-5 py-2 font-medium">Produto</th>
                        <th className="px-5 py-2 font-medium">Campos</th>
                        <th className="px-5 py-2 font-medium">Resumo</th>
                        <th className="px-5 py-2 font-medium">Quando</th>
                        <th className="px-5 py-2" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--color-hairline)]">
                      {items.map((group) => {
                        const open = expandedProduct === group.productId;
                        return (
                          <Fragment key={group.productId}>
                            <tr
                              className="cursor-pointer hover:bg-[var(--color-surface-elevated)]"
                              onClick={() => {
                                setExpandedProduct(open ? null : group.productId);
                                setExpandedOp(null);
                              }}
                            >
                              <td className="px-5 py-2.5 text-[var(--color-ink)]">
                                <div className="flex items-center gap-2">
                                  {open ? (
                                    <ChevronDown className="size-3.5 shrink-0 text-[var(--color-muted)]" />
                                  ) : (
                                    <ChevronRight className="size-3.5 shrink-0 text-[var(--color-muted)]" />
                                  )}
                                  <div>
                                    <p className="font-medium">
                                      {group.productName ?? `Produto #${group.productId}`}
                                    </p>
                                    <p className="text-xs text-[var(--color-muted)]">
                                      #{group.productId} · {group.operations.length} operação
                                      {group.operations.length === 1 ? '' : 'ões'}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-5 py-2.5">
                                <div className="flex flex-wrap gap-1">
                                  {group.fields.map((field) => (
                                    <Badge key={field} tone="neutral">
                                      {FIELD_LABEL[field] ?? field}
                                    </Badge>
                                  ))}
                                </div>
                              </td>
                              <td className="px-5 py-2.5">
                                <GroupStatusSummary group={group} />
                              </td>
                              <td className="px-5 py-2.5 text-xs text-[var(--color-muted)]">
                                {formatDateTime(group.latestCreatedAt)}
                              </td>
                              <td className="px-5 py-2.5" />
                            </tr>
                            {open
                              ? group.operations.map((op) => (
                                  <Fragment key={op.id}>
                                    <tr
                                      className="cursor-pointer bg-[var(--color-surface-soft)] hover:bg-[var(--color-surface-elevated)]"
                                      onClick={() =>
                                        setExpandedOp(expandedOp === op.id ? null : op.id)
                                      }
                                    >
                                      <td className="px-5 py-2 pl-12 text-[var(--color-ink)]">
                                        {op.skuId !== null ? (
                                          <span className="text-xs text-[var(--color-muted)]">
                                            SKU {op.skuId}
                                          </span>
                                        ) : (
                                          <span className="text-xs text-[var(--color-muted)]">
                                            produto
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-5 py-2">
                                        <div className="flex flex-wrap gap-1">
                                          {op.fields.map((field) => (
                                            <Badge key={field} tone="neutral">
                                              {FIELD_LABEL[field] ?? field}
                                            </Badge>
                                          ))}
                                        </div>
                                      </td>
                                      <td className="px-5 py-2">
                                        <div className="flex flex-wrap items-center gap-1">
                                          <Badge tone={STATUS_TONE[op.status] ?? 'neutral'}>
                                            {STATUS_LABEL[op.status] ?? op.status}
                                          </Badge>
                                          {op.dryRun ? <Badge tone="brand">simulação</Badge> : null}
                                          <ScoreDeltaBadge item={op} />
                                        </div>
                                      </td>
                                      <td className="px-5 py-2 text-xs text-[var(--color-muted)]">
                                        {formatDateTime(op.createdAt)}
                                      </td>
                                      <td className="px-5 py-2 text-right">
                                        {can('admin') &&
                                        op.status === 'succeeded' &&
                                        !op.dryRun &&
                                        op.rolledBackAt === null ? (
                                          <Button
                                            variant="secondary"
                                            size="sm"
                                            disabled={rollback.isPending}
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              rollback.mutate(op.id);
                                            }}
                                          >
                                            <Undo2 className="size-3.5" />
                                            Reverter
                                          </Button>
                                        ) : null}
                                      </td>
                                    </tr>
                                    {expandedOp === op.id ? (
                                      <tr>
                                        <td
                                          colSpan={5}
                                          className="bg-[var(--color-surface-soft)] px-5 py-3 pl-12"
                                        >
                                          <ExpandedPayload item={op} />
                                        </td>
                                      </tr>
                                    ) : null}
                                  </Fragment>
                                ))
                              : null}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="divide-y divide-[var(--color-hairline)] md:hidden">
                  {items.map((group) => {
                    const open = expandedProduct === group.productId;
                    return (
                      <div key={group.productId} className="px-4 py-3">
                        <button
                          type="button"
                          className="w-full text-left"
                          onClick={() => {
                            setExpandedProduct(open ? null : group.productId);
                            setExpandedOp(null);
                          }}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium text-[var(--color-ink)]">
                              {group.productName ?? `Produto #${group.productId}`}
                            </p>
                            <GroupStatusSummary group={group} />
                          </div>
                          <p className="mt-1 text-[11px] text-[var(--color-muted)]">
                            #{group.productId} · {formatDateTime(group.latestCreatedAt)} ·{' '}
                            {group.operations.length} operação
                            {group.operations.length === 1 ? '' : 'ões'}
                          </p>
                          <div className="mt-2 flex flex-wrap gap-1">
                            {group.fields.map((field) => (
                              <Badge key={field} tone="neutral">
                                {FIELD_LABEL[field] ?? field}
                              </Badge>
                            ))}
                          </div>
                        </button>
                        {open ? (
                          <div className="mt-3 space-y-2 border-t border-[var(--color-hairline)] pt-3">
                            {group.operations.map((op) => (
                              <div
                                key={op.id}
                                className="rounded-[10px] border border-[var(--color-hairline)] px-3 py-2"
                              >
                                <button
                                  type="button"
                                  className="w-full text-left"
                                  onClick={() =>
                                    setExpandedOp(expandedOp === op.id ? null : op.id)
                                  }
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <p className="text-xs text-[var(--color-muted)]">
                                      {op.skuId !== null ? `SKU ${op.skuId}` : 'produto'} ·{' '}
                                      {formatDateTime(op.createdAt)}
                                    </p>
                                    <Badge tone={STATUS_TONE[op.status] ?? 'neutral'}>
                                      {STATUS_LABEL[op.status] ?? op.status}
                                    </Badge>
                                  </div>
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {op.fields.map((field) => (
                                      <Badge key={field} tone="neutral">
                                        {FIELD_LABEL[field] ?? field}
                                      </Badge>
                                    ))}
                                  </div>
                                </button>
                                {can('admin') &&
                                op.status === 'succeeded' &&
                                !op.dryRun &&
                                op.rolledBackAt === null ? (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    className="mt-2 w-full"
                                    disabled={rollback.isPending}
                                    onClick={() => rollback.mutate(op.id)}
                                  >
                                    <Undo2 className="size-3.5" />
                                    Reverter
                                  </Button>
                                ) : null}
                                {expandedOp === op.id ? (
                                  <div className="mt-3">
                                    <ExpandedPayload item={op} />
                                  </div>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </QueryState>
        </Card>

        {total > 0 ? (
          <PaginationButtons
            className="justify-end"
            page={page}
            pageCount={pageCount}
            onPrev={() => setPage((current) => current - 1)}
            onNext={() => setPage((current) => current + 1)}
          />
        ) : null}
      </div>
    </>
  );
}

function GroupStatusSummary({ group }: { group: WriteOperationProductGroup }) {
  const parts: string[] = [];
  if (group.statusCounts.succeeded > 0) {
    parts.push(
      `${group.statusCounts.succeeded} publicada${group.statusCounts.succeeded === 1 ? '' : 's'}`,
    );
  }
  if (group.statusCounts.failed > 0) {
    parts.push(`${group.statusCounts.failed} falha${group.statusCounts.failed === 1 ? '' : 's'}`);
  }
  if (group.statusCounts.pending > 0) {
    parts.push(`${group.statusCounts.pending} pendente${group.statusCounts.pending === 1 ? '' : 's'}`);
  }
  if (group.statusCounts.rolled_back > 0) {
    parts.push(
      `${group.statusCounts.rolled_back} revertida${group.statusCounts.rolled_back === 1 ? '' : 's'}`,
    );
  }
  if (group.statusCounts.aborted_conflict > 0) {
    parts.push(`${group.statusCounts.aborted_conflict} abortada${group.statusCounts.aborted_conflict === 1 ? '' : 's'}`);
  }
  if (group.statusCounts.dry_run > 0) {
    parts.push(`${group.statusCounts.dry_run} simulação${group.statusCounts.dry_run === 1 ? '' : 'ões'}`);
  }
  return (
    <span className="text-xs text-[var(--color-muted)]">
      {parts.length > 0 ? parts.join(' · ') : `${group.operations.length} operação(ões)`}
    </span>
  );
}

function ScoreDeltaBadge({ item }: { item: WriteOperationSummary }) {
  if (item.scoreBefore == null || item.scoreAfter == null) return null;
  const delta = item.scoreAfter - item.scoreBefore;
  const sign = delta > 0 ? '+' : '';
  return (
    <Badge tone={delta >= 0 ? 'success' : 'critical'}>
      {sign}
      {delta} pts
    </Badge>
  );
}

function ExpandedPayload({ item }: { item: WriteOperationSummary }) {
  const fieldDiffs = buildFieldDiffs(item);

  return (
    <div className="space-y-4">
      {item.error ? (
        <p className="rounded-[10px] border border-[color-mix(in_srgb,var(--color-error)_40%,transparent)] bg-[color-mix(in_srgb,var(--color-error)_12%,transparent)] px-3 py-2 text-xs text-[var(--color-error)]">
          {item.error}
        </p>
      ) : null}

      {fieldDiffs.length > 0 ? (
        <div className="space-y-3">
          {fieldDiffs.map((diff) => (
            <div key={diff.field}>
              <p className="mb-2 text-[11px] font-medium text-[var(--color-muted)]">
                {FIELD_LABEL[diff.field] ?? diff.field}
              </p>
              <div className="grid gap-3 lg:grid-cols-2">
                <Card className="flex flex-col">
                  <div className="border-b border-[var(--color-hairline)] px-4 py-2">
                    <span className="text-xs font-medium text-[var(--color-muted)]">Antes</span>
                  </div>
                  <div className="flex-1 px-4 py-3">
                    <DiffPanel
                      before={diff.before}
                      after={diff.after}
                      side="before"
                      field={diff.field}
                    />
                  </div>
                </Card>
                <Card className="flex flex-col">
                  <div className="border-b border-[var(--color-hairline)] px-4 py-2">
                    <span className="text-xs font-medium text-[var(--color-muted)]">
                      Depois (publicado)
                    </span>
                  </div>
                  <div className="flex-1 px-4 py-3">
                    <DiffPanel
                      before={diff.before}
                      after={diff.after}
                      side="after"
                      field={diff.field}
                    />
                  </div>
                </Card>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div>
        <p className="mb-1 text-[11px] font-medium text-[var(--color-muted)]">
          Dados enviados{item.httpStatus ? ` · HTTP ${item.httpStatus}` : ''}
        </p>
        <pre className="max-h-72 overflow-auto rounded-[10px] border border-[var(--color-hairline)] bg-[var(--color-surface-elevated)] p-3 font-mono text-[11px] text-[var(--color-body)]">
          {JSON.stringify(item.requestPayload, null, 2)}
        </pre>
      </div>
    </div>
  );
}

function buildFieldDiffs(
  item: WriteOperationSummary,
): Array<{ field: string; before: string; after: string }> {
  const snapshot =
    item.preWriteSnapshot && typeof item.preWriteSnapshot === 'object'
      ? (item.preWriteSnapshot as Record<string, unknown>)
      : {};
  const payload =
    item.requestPayload && typeof item.requestPayload === 'object'
      ? (item.requestPayload as Record<string, unknown>)
      : {};
  const byField = new Map(item.items.map((entry) => [entry.field, entry.appliedValue]));

  return item.fields.map((field) => {
    const apiKey = PRODUCT_FIELD_KEYS[field] ?? IMAGE_FIELD_KEYS[field];
    const beforeRaw = apiKey ? snapshot[apiKey] : undefined;
    const afterFromItem = byField.get(field);
    const afterRaw = afterFromItem ?? (apiKey ? payload[apiKey] : undefined);

    return {
      field,
      before: typeof beforeRaw === 'string' ? beforeRaw : beforeRaw == null ? '' : String(beforeRaw),
      after: typeof afterRaw === 'string' ? afterRaw : afterRaw == null ? '' : String(afterRaw),
    };
  });
}
